/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";
import { PrimaryButton } from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useAlert } from "../../store/useAlert";

type TemplateType = "ticket_winner" | "ticket_loser";

type TemplateFields = {
  en_title: string;
  nl_title: string;
  en_body: string;
  nl_body: string;
};

interface TemplateApiResponse extends TemplateFields {
  _id?: string;
  type: TemplateType;
}

const EMPTY_FIELDS: TemplateFields = {
  en_title: "",
  nl_title: "",
  en_body: "",
  nl_body: "",
};

/**
 * Per-section editor for the `ticket_winner` / `ticket_loser` push copy.
 * Plain controlled inputs — react-hook-form's `values` prop wasn't picking
 * up fetched data reliably when the form was conditionally mounted after
 * the initial loading state.
 *
 * Empty fields fall back to the backend's hardcoded defaults — see
 * `mergeTemplate` in `notifications.service.ts`.
 */
function TemplateSection({
  type,
  title,
  description,
}: {
  type: TemplateType;
  title: string;
  description: string;
}) {
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<TemplateFields>(EMPTY_FIELDS);

  useEffect(() => {
    // Note: no `cancelled` guard. The project's axios duplicate-request
    // interceptor dedupes Strict-Mode's double-fire and the real response
    // comes back to the FIRST effect's closure — a `cancelled = true` from
    // the second mount would then silently drop the data. React 18 ignores
    // setState on unmounted components, so this is safe without it.
    (async () => {
      try {
        const response = await axios.get<TemplateApiResponse | null>(
          `notification-templates/${type}`,
        );
        const data = response?.data;
        if (data) {
          setFields({
            en_title: data.en_title ?? "",
            nl_title: data.nl_title ?? "",
            en_body: data.en_body ?? "",
            nl_body: data.nl_body ?? "",
          });
        }
      } catch (error: any) {
        setAlert({
          isOpen: true,
          message:
            error?.response?.data?.message || "Failed to load message",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [type]);

  const onChange = (field: keyof TemplateFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFields((prev) => ({ ...prev, [field]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`notification-templates/${type}`, fields);
      setAlert({
        isOpen: true,
        message: "Message saved",
        severity: "success",
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to save message",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const inputClasses =
    "w-full h-[46px] px-4 bg-sky-lighter border border-gray-border focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none disabled:bg-gray-100 disabled:text-gray-400";

  const textareaClasses =
    "w-full min-h-[120px] px-4 py-[11px] bg-sky-lighter border border-gray-border focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none disabled:bg-gray-100 disabled:text-gray-400";

  const labelClasses = "font-satoshi text-dark text-sm font-medium mb-3";

  return (
    <div className="bg-white rounded-lg p-6 shadow mb-6">
      <h2 className="text-lg font-bold text-dark mb-1">{title}</h2>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div className="mb-4">
            <p className={labelClasses}>Title (English)</p>
            <input
              type="text"
              className={inputClasses}
              placeholder="Message title (English)"
              value={fields.en_title}
              onChange={onChange("en_title")}
              disabled={loading || saving}
            />
          </div>
          <div className="mb-4">
            <p className={labelClasses}>Title (Dutch)</p>
            <input
              type="text"
              className={inputClasses}
              placeholder="Message title (Dutch)"
              value={fields.nl_title}
              onChange={onChange("nl_title")}
              disabled={loading || saving}
            />
          </div>
          <div className="mb-4">
            <p className={labelClasses}>Body (English)</p>
            <textarea
              className={textareaClasses}
              placeholder="Message body (English)"
              value={fields.en_body}
              onChange={onChange("en_body")}
              disabled={loading || saving}
            />
          </div>
          <div className="mb-4">
            <p className={labelClasses}>Body (Dutch)</p>
            <textarea
              className={textareaClasses}
              placeholder="Message body (Dutch)"
              value={fields.nl_body}
              onChange={onChange("nl_body")}
              disabled={loading || saving}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Empty fields fall back to the system default.
        </p>
        <PrimaryButton
          label={loading ? "Loading…" : saving ? "Saving…" : "Save"}
          type="submit"
          disabled={loading || saving}
          className="w-32"
        />
      </form>
    </div>
  );
}

function LotteryNotificationsScreen() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-dark mb-2">
        Lottery result messages
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Edit the text shown to users when their lottery ticket is drawn.
        Saving here only updates the message copy, it does not send anything
        on its own. Notifications are sent automatically when the lottery is
        drawn, using the latest saved text.
      </p>
      <TemplateSection
        type="ticket_winner"
        title="Winner message"
        description="Shown to the user when their ticket wins a lottery."
      />
      <TemplateSection
        type="ticket_loser"
        title="Loser message"
        description="Shown to the user when their ticket does not win."
      />
    </div>
  );
}

export default withProtectedRoute(LotteryNotificationsScreen);
